import type { Id } from "@convex/_generated/dataModel";
import { formatCurrency } from "@plasma/utils/money-formatters";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { Copy, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import {
  type CategoryPaginatedItem,
  type ItemWithRelations,
  type ModifierGroupDetail,
  modifiersUpdateModifierGroup,
} from "~/api-refs";
import { Dialog, DialogContent, DialogTitle } from "~/components/ui/dialog";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { logger } from "~/lib/logger";
import { enumIncludes, parseEnumValue } from "~/lib/parse-enum-value";
import { ProductEvents } from "~/lib/posthog";

import type { ModifierFormSchema } from "../../../../lib/validators/modifiers";
import { Button } from "../../../ui/button";
import { Form } from "../../../ui/form";
import { CloseDialogButton } from "../../items/detail/close-dialog-button";
import { ApplyToCategoriesOrItemsForm } from "../../modifier-modal/apply-to-categories-or-items-form";
import { ModifierFormContent } from "../../modifier-modal/modifier-form";

const MODIFIER_TYPES = ["modifier", "sub_modifier"] as const;
const isModifierType = enumIncludes(MODIFIER_TYPES);

export const ModifierGroupEditPage = ({
  modifierGroup,
  categoriesList,
  itemsList,
}: {
  modifierGroup: NonNullable<ModifierGroupDetail>;
  categoriesList: CategoryPaginatedItem[];
  itemsList: ItemWithRelations[];
}) => {
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const menuId = params.menuId ?? "";

  const [error, setError] = useState<string | null>(null);
  const [categoryItemsValues, setCategoryItemsValues] = useState<{
    categories: string[];
    items: string[];
  }>(() => ({ categories: [], items: [] }));
  const [applyFormChanged, setApplyFormChanged] = useState(false);

  const updateModifierGroupMutation = useMutation({
    mutationFn: (data: {
      modifierGroupId: Id<"modifierGroups">;
      data: {
        name: string;
        minimum: number;
        maximum: number;
        modifiers: {
          name: string;
          additionalCost: number;
          isOutOfStock: boolean;
          canBeMultiplied: boolean;
          sortOrder: number;
        }[];
        subModifierGroups: {
          name: string;
          maximum: number;
          minimum: number;
          sortOrder: number;
          modifiers: {
            name: string;
            additionalCost: number;
            isOutOfStock: boolean;
            canBeMultiplied: boolean;
          }[];
        }[];
      };
      applyModifierGroupTo: {
        categories: Id<"categories">[];
        items: Id<"items">[];
      };
    }) => convexClient.mutation(modifiersUpdateModifierGroup, data),
  });

  const form = useForm<ModifierFormSchema>({
    defaultValues: {
      ...modifierGroup,
      type: parseEnumValue(modifierGroup.type, isModifierType, "modifier"),
      modifiersList: [
        ...modifierGroup.modifiers.map((modifier) => ({
          itemId: modifier._id,
          name: modifier.name,
          accordionOpen: true,
          sortOrder: modifier.sortOrder,
          modifierType: "modifier" as const,
          data: {
            additionalCost: formatCurrency(modifier.additionalCost, {
              hideCurrencySign: true,
            }),
            canBeMultiplied: modifier.canBeMultiplied,
            isOutOfStock: modifier.isOutOfStock,
          },
        })),
        ...modifierGroup.subModifierGroups.map((subModifier) => ({
          itemId: subModifier._id,
          modifierType: "sub_modifier" as const,
          name: subModifier.name,
          sortOrder: subModifier.sortOrder,
          accordionOpen: true,
          data: {
            minimum: subModifier.minimum,
            maximum: subModifier.maximum,
            type: parseEnumValue(
              subModifier.type,
              isModifierType,
              "sub_modifier"
            ),
            parentModifierGroupId: subModifier.parentModifierGroupId ?? null,
            modifiers: subModifier.modifiers.map((modifier) => ({
              itemId: modifier._id,
              name: modifier.name,
              sortOrder: modifier.sortOrder,
              additionalCost: formatCurrency(modifier.additionalCost, {
                hideCurrencySign: true,
              }),
              isOutOfStock: modifier.isOutOfStock,
              canBeMultiplied: modifier.canBeMultiplied,
            })),
          },
        })),
      ],
      modifiers: [],
      subModifierGroups: [],
    },
  });

  const defaultItemsCategories = {
    categories: modifierGroup.categoryRelations.map((category) =>
      toConvexTableId("categories", category.categoryId)
    ),
    items: modifierGroup.itemRelations.map((item) =>
      toConvexTableId("items", item.itemId)
    ),
  };

  const onGoBack = () => {
    void navigate({ to: ".." });
  };

  useEffect(() => {
    return () => {
      form.reset();
    };
  }, [form.reset, form]);

  const handleFormSubmit = async (data: ModifierFormSchema) => {
    if (updateModifierGroupMutation.isPending) {
      return;
    }

    if (
      applyFormChanged &&
      !categoryItemsValues.categories.length &&
      !categoryItemsValues.items.length
    ) {
      setError("Please select at least one category or item");
      return;
    }
    setError(null);

    try {
      await updateModifierGroupMutation.mutateAsync({
        modifierGroupId: modifierGroup._id,
        data: {
          name: data.name,
          minimum: data.minimum,
          maximum: data.maximum,
          modifiers: data.modifiersList
            .filter((modifier) => modifier.modifierType === "modifier")
            .map((modifier) => ({
              name: modifier.name,
              additionalCost: Number.parseFloat(modifier.data.additionalCost),
              isOutOfStock: modifier.data.isOutOfStock,
              canBeMultiplied: modifier.data.canBeMultiplied,
              sortOrder: modifier.sortOrder,
            })),
          subModifierGroups: data.modifiersList
            .filter((modifier) => modifier.modifierType === "sub_modifier")
            .map((submodifier) => ({
              name: submodifier.name,
              maximum: submodifier.data.maximum,
              minimum: submodifier.data.minimum,
              sortOrder: submodifier.sortOrder,
              modifiers: submodifier.data.modifiers.map((modifier) => ({
                name: modifier.name,
                additionalCost: Number.parseFloat(modifier.additionalCost),
                isOutOfStock: modifier.isOutOfStock,
                canBeMultiplied: modifier.canBeMultiplied,
              })),
            })),
        },
        applyModifierGroupTo: applyFormChanged
          ? {
              categories: categoryItemsValues.categories.map((id) =>
                toConvexTableId("categories", id)
              ),
              items: categoryItemsValues.items.map((id) =>
                toConvexTableId("items", id)
              ),
            }
          : defaultItemsCategories,
      });
      ProductEvents.modifierGroupUpdated(modifierGroup._id);
      onGoBack();
    } catch (err) {
      logger.exception("modifier_group_update_failed", err);
    }
  };

  return (
    <Dialog
      onOpenChange={(value: boolean) => {
        if (!value) {
          void navigate({ to: ".." });
        }
      }}
      open
    >
      <DialogContent
        className="h-full max-h-dvh w-full max-w-full overflow-y-hidden rounded-none"
        noCloseButton
      >
        <DialogTitle className="sr-only">Edit modifier group</DialogTitle>
        <Form {...form}>
          <form
            className="h-[calc(100vh-142px)]"
            onSubmit={form.handleSubmit(handleFormSubmit)}
          >
            <div className="bg-muted relative flex h-18 items-center justify-center">
              <h2 className="subtitle text-foreground m-0">
                Edit modifier group
              </h2>
              <CloseDialogButton
                onGoBack={onGoBack}
                onSubmit={form.handleSubmit(handleFormSubmit)}
              />
            </div>
            <div className="relative flex h-full w-full justify-center overflow-y-auto pt-10 pb-27">
              <div className="flex h-auto w-full max-w-4xl flex-col">
                <ModifierFormContent editMode={true} form={form} />
                <div className="h-full">
                  <ApplyToCategoriesOrItemsForm
                    categories={categoriesList}
                    items={itemsList}
                    // Set state is not loading modifiergroup, so we set new values only if form changes
                    setError={setError}
                    setFormChanged={setApplyFormChanged}
                    setValues={setCategoryItemsValues}
                    values={
                      applyFormChanged
                        ? categoryItemsValues
                        : defaultItemsCategories
                    }
                  />
                </div>
                {error && <p className="body-sm text-destructive">{error}</p>}
                <div className="mb-27 pb-12" />
              </div>
            </div>
            <div className="border-border fixed inset-x-0 bottom-0 left-0 inline-flex h-18 justify-between border-t p-3">
              <div className="space-x-3">
                {/* V4.1: Route-based duplicate for URL context tracking */}
                <Button
                  className="space-x-1.5"
                  onClick={(e) => {
                    e.preventDefault();
                    void navigate({
                      to: `/menus/${menuId}/modifiers/${modifierGroup._id}/duplicate`,
                    });
                  }}
                  type="button"
                  variant="secondary"
                >
                  <Copy className="size-4" />
                  <p className="body text-foreground text-weight-medium">
                    Duplicate group
                  </p>
                </Button>
                {/* V4.1: Route-based delete for URL context tracking */}
                <Button
                  className="space-x-1.5"
                  onClick={(e) => {
                    e.preventDefault();
                    void navigate({
                      to: `/menus/${menuId}/modifiers/${modifierGroup._id}/delete`,
                    });
                  }}
                  type="button"
                  variant="ghost"
                >
                  <Trash2 className="size-4" />
                  <p className="text-foreground text-weight-medium">
                    Delete Group
                  </p>
                </Button>
              </div>

              <Button
                disabled={
                  !(form.formState.isDirty || applyFormChanged) ||
                  updateModifierGroupMutation.isPending
                }
                type="submit"
              >
                Save Changes
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
